// ═══════════════════════════════════════════════════════════════════
//  APPOINTMENTS → SEARCH: THE CRITERIA FORM, IN PURE FUNCTIONS
//
//  The form is checked here the way the server checks it
//  (AmsBookingSearchRules.Validate), so the Search button stays off until
//  the server would accept the search, and says why field by field:
//  - a Pacific date range, both ends required and inclusive, at most
//    MAX_RANGE_DAYS (400) long, `from` on or before `to`;
//  - nothing else: every criterion is optional, and a range alone lists
//    every appointment in it, a page at a time (#770, 2026-10-09);
//  - an address with at least one word besides a unit marker or the state,
//    and at most ADDRESS_MAX_WORDS of them;
//  - a phone number, when given, that reduces to ten digits;
//  - an energy account number with something besides spaces and dashes,
//    and a confirmation number of six digits (#771);
//  - a Social Security number of nine digits, either an agency dummy (9xx,
//    searchable since 2026-10-28, AB#771) or one the SSA issues (#771),
//    offered to every appointments user, each search audited (AB#845);
//  - a host (AB#844), picked from the list, so always a Cal user id; a
//    site only narrows the host list and the search, and is sent only with
//    a host.
//
//  The dates travel as the Pacific calendar dates they are ("YYYY-MM-DD",
//  the server's DateOnly); the server turns them into instants. Nothing
//  here reads the browser's zone.
//
//  Adding a plain criterion is one entry in TEXT_CRITERIA plus its field in
//  SearchForm and SearchRequest: validation, the request builder and the
//  form's inputs all walk that list.
//
//  The Social Security number is deliberately NOT in that list. It is
//  masked, every search by it is audited on the server, never put in
//  buildSearchRequest's body (whose result is the search query's cache key)
//  and sent only by buildSsnSearchRequest, through a mutation; see
//  ssnSearch.ts.
// ═══════════════════════════════════════════════════════════════════

import { SITE_TIME_ZONE, addDays, todayInZone } from "@/app/booking-calendar/lib/siteTime"
import { applicableProgramYear } from "@/lib/program-year"
import { BookingRequestStatus, statusLabel } from "@/app/booking-calendar/lib/bookingStatus"
import type { CalOrganizationUser, CalTeam, CalTeamMember } from "@/app/booking-calendar/bookingCalendarApi"
import type { BookingSearchRequest, BookingSsnSearchRequest } from "./searchApi"

/**
 * The longest range the server answers for, both ends included (AmsQueryWindow.SearchMaxDays): 400
 * days, so the default range always fits (AB#817, decision 2026-10-29). At its longest, on
 * September 30 of a program year with a leap day, that default is 366 + DEFAULT_RANGE_DAYS_AHEAD = 394
 * days.
 */
export const MAX_RANGE_DAYS = 400

/**
 * How many Pacific calendar days past today the default range reaches (AB#817, decision
 * 2026-10-29), so the public bookings ahead (up to 28 days out) show without changing the dates.
 */
export const DEFAULT_RANGE_DAYS_AHEAD = 28

/** The page sizes offered. The server caps a page at 200 (AmsBookingSearchCriteria.MaxTake). */
export const PAGE_SIZES = [25, 50] as const
export type PageSize = (typeof PAGE_SIZES)[number]
export const DEFAULT_PAGE_SIZE: PageSize = 25

/**
 * How many results a search by Social Security number asks for, in one page: it is an exact lookup,
 * and paging it would mean sending the number again. The server's most (AmsBookingSearchCriteria.MaxTake).
 */
export const SSN_SEARCH_TAKE = 200

/** The digits in a Social Security number. */
export const SSN_LENGTH = 9

/** The digits in a confirmation code (BookingConfirmationCode). */
export const CONFIRMATION_CODE_LENGTH = 6

/** The server's [StringLength] on the address (AmsBookingSearchRules.AddressMaxLength). */
export const ADDRESS_MAX_LENGTH = 200

/** The most words the server matches an address on (AmsBookingSearchRules.AddressMaxWords). */
export const ADDRESS_MAX_WORDS = 12

/**
 * The free-text criteria, in the order the form shows them after the Social Security number, which
 * comes first (AB#817).
 */
export type TextCriterion = "customerName" | "address" | "phoneNumber" | "confirmationCode" | "email" | "energyAccountNumber" | "referenceCode"

export interface TextCriterionDefinition {
  key: TextCriterion
  label: string
  /** The server's [StringLength] on the request field, so the input cannot overrun it. */
  maxLength: number
  inputType: "text" | "tel" | "email"
  inputMode?: "numeric"
  autoComplete: "off"
  placeholder?: string
  hint?: string
}

/**
 * In the product owner's order (AB#817): after the Social Security number, which the form places
 * first, four columns wide that reads
 *   Social Security number | Customer name | Address               | Phone number
 *   Confirmation number    | Email         | Energy account number | Booking reference
 */
export const TEXT_CRITERIA: readonly TextCriterionDefinition[] = [
  { key: "customerName", label: "Customer name", maxLength: 200, inputType: "text", autoComplete: "off", hint: "Part of the first or last name." },
  { key: "address", label: "Address", maxLength: ADDRESS_MAX_LENGTH, inputType: "text", autoComplete: "off", hint: 'Part of the street address, e.g. "Tobin St" or a ZIP code.' },
  { key: "phoneNumber", label: "Phone number", maxLength: 30, inputType: "tel", autoComplete: "off", placeholder: "(206) 555-0100" },
  { key: "confirmationCode", label: "Confirmation number", maxLength: CONFIRMATION_CODE_LENGTH, inputType: "text", inputMode: "numeric", autoComplete: "off", hint: "Six digits." },
  { key: "email", label: "Email", maxLength: 255, inputType: "email", autoComplete: "off", hint: "The whole address." },
  { key: "energyAccountNumber", label: "Energy account number", maxLength: 64, inputType: "text", autoComplete: "off", hint: "The whole number; spaces, dashes and case are ignored." },
  { key: "referenceCode", label: "Booking reference", maxLength: 64, inputType: "text", autoComplete: "off", hint: "The whole reference." },
]

/**
 * Every status a booking request can hold, in the order staff read them — the numbers the server
 * stores and accepts. 8 is retired and is not offered.
 */
export const SEARCHABLE_STATUSES: readonly number[] = [
  BookingRequestStatus.Booked,
  BookingRequestStatus.Incomplete,
  BookingRequestStatus.Complete,
  BookingRequestStatus.NoShow,
  BookingRequestStatus.Cancelled,
  BookingRequestStatus.Rescheduled,
  BookingRequestStatus.VoluntaryWithdrawal,
  BookingRequestStatus.FailedToProvideDocs,
  BookingRequestStatus.Other,
  BookingRequestStatus.Invalid,
  BookingRequestStatus.SystemError,
  BookingRequestStatus.Screened,
  BookingRequestStatus.Refused,
  BookingRequestStatus.Abandoned,
  BookingRequestStatus.Expired,
]

export interface SearchForm extends Record<TextCriterion, string> {
  /** "YYYY-MM-DD", a Pacific calendar date, inclusive. */
  from: string
  /** "YYYY-MM-DD", a Pacific calendar date, inclusive. */
  to: string
  statuses: number[]
  /**
   * The host picked (AB#844): their Cal user id as a string, or "" for anyone. A criterion on its
   * own, with the range.
   */
  hostCalUserId: string
  /**
   * The site the host list is drawn from, as its Cal team slug, or "" for every site. Narrows the
   * search to that site only together with a host; on its own it only narrows the list.
   */
  siteSlug: string
  /**
   * A Social Security number, as typed. Held only until Search is chosen — the page clears it as it sends — and sent only by
   * buildSsnSearchRequest.
   */
  ssn: string
}

/** The fields a message can belong to; `range` is the from–to pair taken together. */
export type SearchFormField = TextCriterion | "ssn" | "hostCalUserId" | "from" | "to" | "range"

export interface SearchFormValidation {
  valid: boolean
  errors: Partial<Record<SearchFormField, string>>
}

/**
 * The first day of the program year `today` falls in: October 1, since program years run October
 * through September (applicableProgramYear names one by the year it ends in).
 */
export function programYearStart(today: string): string {
  const programYear = applicableProgramYear(today) ?? Number(today.slice(0, 4))
  return `${programYear - 1}-10-01`
}

/**
 * An empty form over the default range (AB#817, decisions 2026-10-28 and 2026-10-29): October 1 of
 * the current program year through DEFAULT_RANGE_DAYS_AHEAD days after today, both Pacific, so the
 * bookings ahead are included. It rolls over by itself each October 1, and at most 366 + 28 = 394
 * days long it is always within MAX_RANGE_DAYS.
 */
export function defaultSearchForm(now: Date = new Date(), timeZone: string = SITE_TIME_ZONE): SearchForm {
  const today = todayInZone(timeZone, now)
  return {
    from: programYearStart(today),
    to: addDays(today, DEFAULT_RANGE_DAYS_AHEAD),
    customerName: "",
    address: "",
    phoneNumber: "",
    email: "",
    referenceCode: "",
    energyAccountNumber: "",
    confirmationCode: "",
    statuses: [],
    hostCalUserId: "",
    siteSlug: "",
    ssn: "",
  }
}

const CALENDAR_DATE = /^(\d{4})-(\d{2})-(\d{2})$/

/** A "YYYY-MM-DD" as a day number, or undefined when it is not a real date. Calendar arithmetic, in UTC. */
function dayNumber(value: string): number | undefined {
  const match = CALENDAR_DATE.exec(value.trim())
  if (!match) return undefined
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])]
  const date = new Date(Date.UTC(year, month - 1, day))
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return undefined
  return date.getTime() / 86_400_000
}

/** How many calendar days `from`..`to` covers, both ends included; undefined unless both are dates. */
export function daysCovered(from: string, to: string): number | undefined {
  const start = dayNumber(from)
  const end = dayNumber(to)
  return start === undefined || end === undefined ? undefined : end - start + 1
}

/**
 * Ten digits, no country code — the server's BookingPhoneNumber.Normalize — or undefined when the
 * number would not match anything (too short, too long, or one digit repeated, which is a placeholder).
 */
export function normalizePhone(value: string): string | undefined {
  let digits = value.replace(/\D/g, "")
  if (digits.length === 11 && digits.startsWith("1")) digits = digits.slice(1)
  if (digits.length !== 10) return undefined
  if (/^(\d)\1*$/.test(digits)) return undefined
  return digits
}

/**
 * The nine digits of a Social Security number — the server's AmsBookingSearchRules.SsnDigits — or
 * undefined when it is not one: only digits, spaces and dashes, and exactly nine digits.
 */
export function ssnDigits(value: string): string | undefined {
  const trimmed = value.trim()
  if (!/^[0-9 -]+$/.test(trimmed)) return undefined
  const digits = trimmed.replace(/[^0-9]/g, "")
  return digits.length === SSN_LENGTH ? digits : undefined
}

/**
 * A Social Security number as the field shows it while it is typed (AB#817): only its digits, nine at
 * most, with the dashes put in as the digits reach them — "1234" reads "123-4", nine read "123-45-6789".
 * Anything else typed or pasted, spaces and dashes included, is dropped first. Backspacing the digit
 * after a dash drops that dash too, so the digit before it stays; backspacing a dash mid-number puts
 * it straight back. The caret is not managed, so an edit mid-number moves it to the end. Acceptable,
 * and simpler than managing the caret.
 */
export function formatSsnInput(value: string): string {
  const digits = value.replace(/\D/g, "").slice(0, SSN_LENGTH)
  if (digits.length <= 3) return digits
  if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`
}

/**
 * Why the server would refuse this Social Security number, or undefined when it would search it.
 * None of these messages quotes what was typed.
 */
export function ssnProblem(value: string): string | undefined {
  const digits = ssnDigits(value)
  if (digits === undefined) return "Enter the nine digits of the Social Security number."
  // Agency dummy numbers (900–999) are searched like any other (AB#771, decision 2026-10-28).
  if (digits.startsWith("9")) return undefined
  const area = Number(digits.slice(0, 3))
  const group = Number(digits.slice(3, 5))
  const serial = Number(digits.slice(5))
  if (area === 0 || area === 666 || group === 0 || serial === 0) return "That is not a number the SSA issues, so it cannot be searched."
  return undefined
}

/** The energy account number as the server compares it: spaces and dashes removed, upper-cased. */
export function normalizeEnergyAccountNumber(value: string): string {
  return value.trim().replace(/[ -]/g, "").toUpperCase()
}

function hasText(value: string): boolean {
  return value.trim().length > 0
}

/** Whether the form names a host to search by (AB#844): a criterion of its own, with the range. */
export function isHostSearch(form: SearchForm): boolean {
  return hasText(form.hostCalUserId)
}

/** Whether the form names a Social Security number to search by. */
export function isSsnSearch(form: SearchForm): boolean {
  return hasText(form.ssn)
}

/**
 * The unit markers the server drops from an address before matching (BookingAddressNormalizer's
 * UnitDesignators): "Apt 4B" and "#4B" both search for 4B.
 */
const UNIT_MARKERS = new Set([
  "APT", "APARTMENT", "UNIT", "STE", "SUITE", "RM", "ROOM", "BLDG", "BLD", "BUILDING",
  "SPC", "SP", "SPACE", "TRLR", "TRAILER", "LOT", "FL", "FLR", "FLOOR", "#",
])

/**
 * The state words the server drops from an address before matching (BookingAddressNormalizer's
 * StateTokens): every address is in Washington, and many are stored without it.
 */
const STATE_WORDS = new Set(["WA", "WASH", "WASHINGTON"])

/**
 * The words the server would match an address on, as BookingAddressNormalizer.SearchTokens splits
 * them: letters and digits (a hyphen inside a word kept), `#` on its own, everything else a
 * separator (parentheses and apostrophes included), unit markers and state words dropped, repeats
 * dropped. Only counted here; the server does the matching, abbreviations included.
 */
export function addressWords(value: string): string[] {
  const words = (value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toUpperCase().match(/#|[A-Z0-9][A-Z0-9-]*/g) ?? [])
    .map(word => word.replace(/-+$/, ""))
    .filter(word => word.length > 0 && !UNIT_MARKERS.has(word) && !STATE_WORDS.has(word))
  return [...new Set(words)]
}

/** Checks the form as the server would, with a message per field that is wrong. */
export function validateSearchForm(form: SearchForm): SearchFormValidation {
  const errors: SearchFormValidation["errors"] = {}

  const from = dayNumber(form.from)
  const to = dayNumber(form.to)
  if (from === undefined) errors.from = "Choose the first date to search."
  if (to === undefined) errors.to = "Choose the last date to search."

  if (from !== undefined && to !== undefined) {
    if (to < from) {
      errors.range = "The last date must be on or after the first."
    } else if (to - from + 1 > MAX_RANGE_DAYS) {
      errors.range = `The date range must be no longer than ${MAX_RANGE_DAYS} days.`
    }
  }

  if (hasText(form.address)) {
    const words = addressWords(form.address).length
    if (words === 0) errors.address = "Enter part of the address: a street, number or ZIP code."
    else if (words > ADDRESS_MAX_WORDS) errors.address = `Enter no more than ${ADDRESS_MAX_WORDS} words of the address.`
  }

  if (hasText(form.phoneNumber) && normalizePhone(form.phoneNumber) === undefined) {
    errors.phoneNumber = "Enter a ten-digit phone number."
  }

  if (hasText(form.energyAccountNumber) && normalizeEnergyAccountNumber(form.energyAccountNumber) === "") {
    errors.energyAccountNumber = "Enter the account number's letters or digits."
  }

  if (hasText(form.confirmationCode) && !/^[0-9]{6}$/.test(form.confirmationCode.trim())) {
    errors.confirmationCode = "Enter the six-digit confirmation number."
  }

  // Picked from a list, so this only fails if something other than the picker wrote it: the server
  // takes a positive Cal user id.
  if (isHostSearch(form) && !/^[1-9][0-9]*$/.test(form.hostCalUserId.trim())) {
    errors.hostCalUserId = "Choose a host from the list."
  }

  if (isSsnSearch(form)) {
    const problem = ssnProblem(form.ssn)
    if (problem) errors.ssn = problem
  }

  return { valid: Object.keys(errors).length === 0, errors }
}

/**
 * The body of `POST api/ams/booking-requests/search`: the range as given, every text criterion
 * trimmed and left out when empty, the statuses as the numbers the server stores, the host (with
 * its site, when one is chosen; never the site alone), and the page.
 * Never the Social Security number, whatever the form holds: this body is the search query's
 * cache key. A search by one is built by buildSsnSearchRequest.
 */
export function buildSearchRequest(form: SearchForm, page = 0, pageSize: number = DEFAULT_PAGE_SIZE): BookingSearchRequest {
  const request: BookingSearchRequest = {
    from: form.from.trim(),
    to: form.to.trim(),
    skip: Math.max(0, page) * pageSize,
    take: pageSize,
  }
  for (const { key } of TEXT_CRITERIA) {
    const value = form[key].trim()
    if (value) request[key] = value
  }
  if (form.statuses.length > 0) {
    request.statuses = [...new Set(form.statuses)].sort((a, b) => a - b)
  }
  if (isHostSearch(form)) {
    request.hostCalUserId = form.hostCalUserId.trim()
    const site = form.siteSlug.trim()
    if (site) request.siteSlug = site
  }
  return request
}

/**
 * The body of a search by Social Security number, sent only through the `searchBookingRequestsBySsn`
 * mutation: the rest of the criteria as buildSearchRequest builds them, the number as its nine digits,
 * and one page of SSN_SEARCH_TAKE, so nothing ever needs to send it again to page.
 */
export function buildSsnSearchRequest(form: SearchForm): BookingSsnSearchRequest {
  return { ...buildSearchRequest(form, 0, SSN_SEARCH_TAKE), ssn: ssnDigits(form.ssn) ?? form.ssn.trim() }
}

/** What the status picker's button says. */
export function statusSummary(statuses: readonly number[]): string {
  if (statuses.length === 0) return "Any status"
  if (statuses.length <= 2) return statuses.map(statusLabel).join(", ")
  return `${statuses.length} statuses`
}

/** The statuses with `status` switched on or off. */
export function toggleStatus(statuses: readonly number[], status: number, on: boolean): number[] {
  const rest = statuses.filter(value => value !== status)
  return on ? [...rest, status] : rest
}

/** One choice in the host or site picker (AB#844): the value sent, and what the list shows. */
export interface PickerOption {
  value: string
  label: string
}

/** The sites the host picker offers: every team Cal has slugged, by name. */
export function siteOptions(teams: readonly CalTeam[]): PickerOption[] {
  return teams
    .filter(team => !!team.slug)
    .map(team => ({ value: team.slug!, label: team.name }))
    .sort((a, b) => a.label.localeCompare(b.label))
}

/**
 * The hosts the picker offers (AB#844), named as the availability and Move Staff pages name them:
 * with a site chosen, its accepted members; with none, every user in the Cal organization. Each by
 * Cal user id, the value the search sends; by name, then email, then id.
 */
export function hostOptions(calUsers: readonly CalOrganizationUser[], members?: readonly CalTeamMember[]): PickerOption[] {
  const byId = new Map(calUsers.map(user => [user.id, user]))
  const ids = members
    ? [...new Set(members.filter(member => member.accepted).map(member => member.userId))]
    : calUsers.map(user => user.id)
  return ids
    .map(id => {
      const user = byId.get(id)
      return { value: String(id), label: user?.name || user?.email || `Cal user ${id}` }
    })
    .sort((a, b) => a.label.localeCompare(b.label))
}
